// What the app's tours wait for, kept outside the store (no re-render of the app for them):
// - a replay asked for (Asetukset → Mitä uutta → "Näytä esittely uudelleen": shell/tour/whats-new.settings.tsx);
// - a card session in its middle: StudySession says when its first card has been revealed or graded, and when it
//   ends (unmounts). A tour never starts over a deck someone has begun; a deck still at its first card, untouched,
//   is not "in the middle" (the tour may come before it).
// - the startup's audio download screen (AudioGate.tsx) is up (the update check and the warm-up: startup-gates.ts).
// Tours that start by themselves are armed by main.tsx, so a unit test that renders the shell never meets one
// (like the warm-up); a replay works armed or not.
import { useSyncExternalStore } from 'react';

const listeners = new Set<() => void>();
const emit = () => {
  for (const l of listeners) l();
};
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

let replay: string | null = null;
let touched = false;
let audioGate = false;
let armed = false;

/** main.tsx: the tours may start by themselves in this page. */
export function armTours(on = true): void {
  armed = on;
}

export const toursArmed = (): boolean => armed;

/** Show the tour `id` again, from its first step (the next time its screen is ready). */
export function requestTour(id: string): void {
  replay = id;
  emit();
}

/** The replay was taken. */
export function clearTourRequest(): void {
  if (replay === null) return;
  replay = null;
  emit();
}

export const tourRequest = (): string | null => replay;

/** StudySession: its first card was revealed or graded (true), or it ended (false). */
export function setCardsTouched(v: boolean): void {
  if (touched === v) return;
  touched = v;
  emit();
}

export const cardsTouched = (): boolean => touched;

/** AudioGate: its screen is up or gone. */
export function setAudioGateUp(v: boolean): void {
  if (audioGate === v) return;
  audioGate = v;
  emit();
}

export const audioGateUp = (): boolean => audioGate;

export function useTourSignals(): { replay: string | null; touched: boolean; audioGate: boolean } {
  const r = useSyncExternalStore(subscribe, tourRequest, () => null);
  const t = useSyncExternalStore(subscribe, cardsTouched, () => false);
  const a = useSyncExternalStore(subscribe, audioGateUp, () => false);
  return { replay: r, touched: t, audioGate: a };
}
